/**
 * Audio Proxy Utility for handling S3 audio URLs through backend proxy
 * This solves CORS issues when loading audio files from S3
 */

import { getApiUrl } from '../config/api';

export class AudioProxyUtil {
  private static getBaseUrl() {
    // Extract just the base URL without any path
    return getApiUrl('').replace(/\/$/, '');
  }
  
  /**
   * Converts an S3 audio URL to use the backend audio proxy
   * This bypasses CORS issues by routing through your authenticated backend
   * 
   * @param s3AudioUrl - Original S3 audio URL
   * @returns Proxied URL that works without CORS issues
   */
  static getProxiedAudioUrl(s3AudioUrl: string): string {
    if (!s3AudioUrl) {
      console.warn('Empty audio URL provided to getProxiedAudioUrl');
      return s3AudioUrl;
    }
    
    // If it's not an S3 URL, return as-is
    if (!s3AudioUrl.includes('amazonaws.com') && !s3AudioUrl.includes('s3.')) {
      console.log('Non-S3 URL, returning as-is:', s3AudioUrl);
      return s3AudioUrl;
    }
    
    // Convert to proxied URL
    const proxiedUrl = `${this.getBaseUrl()}/api/proxy-audio?url=${encodeURIComponent(s3AudioUrl)}`;
    console.log('🎵 Converting S3 audio URL to proxied URL:', {
      original: s3AudioUrl,
      proxied: proxiedUrl
    });
    
    return proxiedUrl;
  }
  
  /**
   * Creates an Audio element with proper authentication headers for proxied URLs
   * 
   * @param s3AudioUrl - Original S3 audio URL
   * @returns Promise<HTMLAudioElement> - Configured audio element
   */
  static async createAuthenticatedAudio(s3AudioUrl: string): Promise<HTMLAudioElement> {
    const proxiedUrl = this.getProxiedAudioUrl(s3AudioUrl);
    const audio = new Audio();
    const token = localStorage.getItem('authToken');
    
    console.log('🎵 Creating authenticated audio element:', {
      originalUrl: s3AudioUrl,
      proxiedUrl: proxiedUrl,
      hasToken: !!token
    });
    
    // Add authentication header if we have a token
    if (token && proxiedUrl.includes('/api/proxy-audio')) {
      // For proxied URLs, we need to fetch with auth headers first
      try {
        console.log('🔐 Fetching proxied audio with authentication...');
        
        const response = await fetch(proxiedUrl, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          mode: 'cors',
          credentials: 'omit'
        });
        
        console.log('📡 Proxied audio fetch response:', {
          status: response.status,
          statusText: response.statusText,
          contentType: response.headers.get('content-type')
        });
        
        if (response.ok) {
          const audioBlob = await response.blob();
          audio.src = URL.createObjectURL(audioBlob);
          console.log('✅ Successfully created audio blob URL');
        } else {
          const errorText = await response.text();
          console.error('❌ Failed to fetch proxied audio:', response.statusText, errorText);
          console.log('🔄 Falling back to original URL');
          audio.src = s3AudioUrl; // Fallback to original URL
        }
      } catch (error) {
        console.error('❌ Error fetching proxied audio:', error);
        console.log('🔄 Falling back to original URL');
        audio.src = s3AudioUrl; // Fallback to original URL
      }
    } else {
      // For non-proxied URLs, set directly
      console.log('📄 Setting audio src directly (no proxy needed)');
      audio.src = proxiedUrl;
    }
    
    return audio;
  }
  
  /**
   * Loads audio buffer for Web Audio API with authentication
   * 
   * @param s3AudioUrl - Original S3 audio URL
   * @param audioContext - Web Audio API context
   * @returns Promise<AudioBuffer>
   */
  static async loadAuthenticatedAudioBuffer(
    s3AudioUrl: string, 
    audioContext: AudioContext
  ): Promise<AudioBuffer> {
    const proxiedUrl = this.getProxiedAudioUrl(s3AudioUrl);
    const token = localStorage.getItem('authToken');
    
    console.log('🔐 Loading authenticated audio buffer:', {
      originalUrl: s3AudioUrl,
      proxiedUrl: proxiedUrl,
      hasToken: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'none'
    });
    
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('🌐 Making authenticated request to proxy with headers:', Object.keys(headers));
      
      const response = await fetch(proxiedUrl, {
        method: 'GET',
        headers: headers,
        mode: 'cors',
        credentials: 'omit'
      });
      
      console.log('📡 Proxy response:', {
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries())
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Proxy request failed:', {
          status: response.status,
          statusText: response.statusText,
          errorBody: errorText
        });
        
        // If it's a 403 Forbidden, try to provide helpful debugging
        if (response.status === 403) {
          console.error('🔒 Authentication failed. Check:', {
            hasToken: !!token,
            tokenLength: token?.length || 0,
            isTokenValid: token && token.length > 10,
          });
        }
        
        throw new Error(`Failed to fetch audio: ${response.status} ${response.statusText} - ${errorText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      console.log('✅ Successfully fetched audio buffer, size:', arrayBuffer.byteLength, 'bytes');
      
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      console.log('🎵 Successfully decoded audio buffer:', {
        duration: audioBuffer.duration.toFixed(2) + 's',
        channels: audioBuffer.numberOfChannels,
        sampleRate: audioBuffer.sampleRate
      });
      
      return audioBuffer;
      
    } catch (error) {
      console.error('❌ Error loading authenticated audio buffer:', error);
      
      // Provide helpful error messages based on error type
      if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
        console.error('🌐 Network error - check if backend is running and accessible');
      } else if (error instanceof DOMException && error.name === 'EncodingError') {
        console.error('🎵 Audio decoding error - file might be corrupted or in unsupported format');
      }
      
      throw error;
    }
  }
  
  /**
   * Utility to check if a URL needs proxying
   * 
   * @param url - URL to check
   * @returns boolean - true if URL needs proxying
   */
  static needsProxying(url: string): boolean {
    return url.includes('amazonaws.com') || url.includes('s3.');
  }
}

// Export convenience functions for easy use
export const getProxiedAudioUrl = AudioProxyUtil.getProxiedAudioUrl.bind(AudioProxyUtil);
export const createAuthenticatedAudio = AudioProxyUtil.createAuthenticatedAudio.bind(AudioProxyUtil);
export const loadAuthenticatedAudioBuffer = AudioProxyUtil.loadAuthenticatedAudioBuffer.bind(AudioProxyUtil);
export const needsAudioProxying = AudioProxyUtil.needsProxying.bind(AudioProxyUtil);
